/**
 * What the reminders sheet shows for this phone (v1 ticket 38), as one DOM-free decision from the facts the page
 * reads (`reminders.ts` gathers them), so the rules are tested without a browser. Also the presets for the time, the
 * iPhone check and how the device's id is remembered per learner.
 */
export interface ReminderDevice { id: string; channel: 'push' | 'sync'; confirmed: boolean }

/** `GET /api/reminders` (and what PUT settings, offer and resume answer). */
export interface ReminderSettings {
  publicKey: string;
  time: string;
  card: boolean;
  /** Beta only: whether an army's arrival and a raid's result are pushed. Absent where there are no raids. */
  raidAlerts?: boolean;
  paused: boolean;
  offered: boolean;
  devices: ReminderDevice[];
}

export interface DeviceFacts {
  /** Service worker, notifications and push exist here, and a service worker is registered (not in dev mode). */
  supported: boolean;
  /** An iPhone or iPad. */
  ios: boolean;
  /** Running as the installed home-screen app. */
  standalone: boolean;
  permission: 'default' | 'granted' | 'denied';
  /** The device id this phone remembers for the signed-in learner. */
  remembered: string | null;
  /** The devices the server lists for the learner. */
  devices: ReminderDevice[];
}

export type DeviceState =
  | 'unsupported' // nothing to press
  | 'ios' // add to the home screen first
  | 'denied' // blocked in the browser's settings
  | 'off' // Turn on
  | 'stopped' // this phone remembers a device the server doesn't (or never heard from): Turn on again
  | 'on-push'
  | 'on-sync';

export function deviceState(f: DeviceFacts): DeviceState {
  if (f.ios && !f.standalone) return 'ios';
  if (!f.supported) return 'unsupported';
  // A learner can revoke notification access after setup. The server may still
  // have a confirmed subscription, but this phone cannot show its delivery.
  if (f.permission === 'denied') return 'denied';
  const mine = f.remembered ? f.devices.find((d) => d.id === f.remembered) : undefined;
  if (mine) {
    if (f.permission !== 'granted') return 'stopped';
    if (mine.channel === 'sync') return 'on-sync';
    if (mine.confirmed) return 'on-push';
    return 'stopped';
  }
  if (f.remembered) return 'stopped';
  return 'off';
}

/** A schedule is useful only when this phone or another registered device can deliver it. */
export function canConfigureReminders(f: DeviceFacts): boolean {
  const thisPhoneCanDeliver = f.supported && !(f.ios && !f.standalone) && f.permission !== 'denied';
  const anotherPhoneCanDeliver = f.devices.some((d) => d.id !== f.remembered && (d.channel === 'sync' || d.confirmed));
  return thisPhoneCanDeliver || anotherPhoneCanDeliver;
}

/** Whether this phone could take reminders at all (the first-round offer is only worth showing then). */
export const canRemind = (f: Pick<DeviceFacts, 'supported' | 'ios' | 'standalone'>): boolean => f.supported && !(f.ios && !f.standalone);

/** The first-round offer: the server hasn't had it used or dismissed, this phone could, and it has none yet. */
export const offerDue = (s: Pick<ReminderSettings, 'offered'>, f: Pick<DeviceFacts, 'supported' | 'ios' | 'standalone' | 'remembered'>): boolean =>
  !s.offered && canRemind(f) && !f.remembered;

export function isIos(userAgent: string, touchPoints: number): boolean {
  return /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1);
}

/** The time presets, each a moment of the day (`reminder.preset.<id>`); the default is the way home. */
export const PRESETS = [
  { id: 'morning', time: '08:00' },
  { id: 'lunch', time: '13:00' },
  { id: 'home', time: '18:00' },
  { id: 'dinner', time: '21:00' },
] as const;

export const DEFAULT_TIME = '18:00';

/** Which radio a saved time sits on: a preset's id, or `other`. */
export function presetOf(time: string): (typeof PRESETS)[number]['id'] | 'other' {
  return PRESETS.find((p) => p.time === time)?.id ?? 'other';
}

export const validTime = (s: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(s);

/** Device ids this phone remembers, by learner: `{ [owner]: id }` in localStorage (`li-reminder-device`). */
export function rememberedDevice(raw: string | null, owner: string | null): string | null {
  if (!owner) return null;
  try {
    const v = JSON.parse(raw ?? '{}') as Record<string, unknown>;
    return typeof v[owner] === 'string' && v[owner] ? (v[owner] as string) : null;
  } catch {
    return null;
  }
}

/** The stored map with this learner's id set, or dropped when `id` is null. */
export function withDevice(raw: string | null, owner: string, id: string | null): string {
  let v: Record<string, unknown> = {};
  try { v = JSON.parse(raw ?? '{}') as Record<string, unknown>; } catch { /* start again */ }
  if (!v || typeof v !== 'object' || Array.isArray(v)) v = {};
  if (id) v[owner] = id;
  else delete v[owner];
  return JSON.stringify(v);
}

/** A VAPID public key (URL-safe base64) as the bytes `pushManager.subscribe` wants. */
export function keyBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
